import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Flex } from '@optiaxiom/react';

import { useInstanceId } from '@frontend/hooks';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import { useSpecializedAgentExecutions } from '../../../hooks/useAgentExecutions';
import { agentExecutionsService } from '../../../services/agentExecutions';

import { SpecializedAgentExecutionsTable } from './SpecializedAgentExecutionsTable';

type SpecializedAgentExecutionsTabProps = {
  agentId: string;
  selectedExecutionId?: string | null;
  onExecutionSelect?: (
    executionId: string | null,
    execution?: SpecializedAgentExecution | null
  ) => void;
  baselineEvaluationScore?: number;
};

export function SpecializedAgentExecutionsTab({
  agentId,
  selectedExecutionId,
  onExecutionSelect,
  baselineEvaluationScore,
}: SpecializedAgentExecutionsTabProps) {
  const { instanceId } = useInstanceId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedExecution, setSelectedExecution] = useState<SpecializedAgentExecution | null>(
    null
  );
  const [isSearchingForExecution, setIsSearchingForExecution] = useState(false);
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const { data, isPending, error } = useSpecializedAgentExecutions(
    instanceId || '',
    agentId,
    currentPage,
    20 // page size
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', page.toString());
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleExecutionSelect = (execution: SpecializedAgentExecution) => {
    setSelectedExecution(execution);
    onExecutionSelect?.(execution.id, execution);
  };

  // Function to search for execution across different pages
  const searchForExecutionInPages = useCallback(
    () => async () => {
      if (!selectedExecutionId || !instanceId || !agentId) return;

      // Try pages 1-5 to find the execution
      for (let page = 1; page <= 5; page++) {
        try {
          const pageData = await agentExecutionsService.getAgentExecutions(
            instanceId,
            agentId,
            page,
            20
          );

          if (pageData?.items) {
            const execution = pageData.items.find(
              (exec: SpecializedAgentExecution) => exec.id === selectedExecutionId
            );
            if (execution) {
              setSelectedExecution(execution);
              setIsSearchingForExecution(false);
              // Notify parent component about the found execution
              onExecutionSelect?.(execution.id, execution);
              return;
            }
          }
        } catch (error) {
          console.error('Error searching for execution on page', page, error);
        }
      }

      // If not found after searching multiple pages
      console.warn('Execution not found in first 5 pages:', selectedExecutionId);
      setIsSearchingForExecution(false);
    },
    [agentId, instanceId, onExecutionSelect, selectedExecutionId]
  );

  // Note: Individual execution endpoint doesn't exist, so we only use executions from the current page

  // Effect to find and set the selected execution when selectedExecutionId changes or data loads
  useEffect(() => {
    if (selectedExecutionId) {
      if (data?.items && !isPending) {
        // Data is loaded, try to find the execution
        const execution = data.items.find(exec => exec.id === selectedExecutionId);
        if (execution) {
          setSelectedExecution(execution);
          setIsSearchingForExecution(false);
        } else if (!isSearchingForExecution) {
          // Execution not found in current page - search other pages
          setIsSearchingForExecution(true);
          searchForExecutionInPages();
        }
      }
      // If data is still loading (isPending=true), don't do anything yet - wait for data
    } else {
      setSelectedExecution(null);
      setIsSearchingForExecution(false);
    }
  }, [
    selectedExecutionId,
    data?.items,
    isPending,
    isSearchingForExecution,
    searchForExecutionInPages,
  ]);

  return (
    <Flex h="full" w="full">
      <SpecializedAgentExecutionsTable
        executions={data?.items || []}
        pagination={data?.pagination}
        isLoading={isPending}
        error={error}
        onExecutionSelect={handleExecutionSelect}
        selectedExecutionId={selectedExecution?.id || selectedExecutionId}
        onPageChange={handlePageChange}
        baselineEvaluationScore={baselineEvaluationScore}
      />
    </Flex>
  );
}
